"use client";

import React from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
const Button = dynamic(() => import("@/Components/Button"));
import { Splide, SplideSlide } from "@splidejs/react-splide";
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";
import "@splidejs/react-splide/css";

interface Props {
  data: any;
  viewCase?: boolean;
}
const RequestProposalSolution: React.FC<Props> = ({
  data,
  viewCase = true,
}) => {
  const url = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL;
  const router = useRouter();

  const handleClick = () => router.push("/case-studies");

  return (
    <section className="relative z-[0] overflow-hidden py-[56px] lg:py-[80px] xl:py-[100px] bg-white">
      <div className="container">
        {/* Heading */}
        <div className="heading mb-[40px]">
          <h2 className="text-heading-1 font-bold text-primary-text text-center">
            {data.title}
          </h2>
          {viewCase && (
            <p className="text-[#32343B] text-[20px] leading-[26px] xl:text-[24px] xl:leading-[31.1px] text-center">
              {data.short_title ?? ""}
            </p>
          )}
        </div>

        {/* Case Studies */}
        {data?.case_studies?.data?.map((item: any, i: number) => (
          <div
            key={"Projects" + i}
            onClick={() =>
              router.push(`/case-studies/${item?.attributes?.slug}`)
            }
            className="bg-white lg:p-[48px_24px] md:p-[24px_24px] p-[24px_12px] border-[#B0D9F7] border-[1px] group md:mb-[30px] mb-[20px] cursor-pointer"
          >
            {/* Title & Key Points */}
            <div className="grid grid-cols-1 xl:grid-cols-[60%_auto] 2xl:grid-cols-[70%_auto] mb-[24px] lg:mb-[32px] lg:gap-[16px]">
              <div>
                <h2 className="text-[#040405] font-[700] text-[28px] lg:text-[32px] xl:text-[36px] leading-[36px] lg:leading-[46.6px] xl:leading-[46.8px] mb-[20px] tracking-[1px]">
                  {item?.attributes?.title ?? ""}
                </h2>
              </div>

              <div className="flex justify-center xl:justify-end md:gap-[16px] gap-[8px] xl:gap-[30px]">
                {item?.attributes?.blocks?.[0]?.key_points?.map(
                  (project: any, idx: number) => (
                    <div
                      key={idx}
                      className={`p-[13px_11px] sm:p-[20px_12px] flex flex-col w-full max-w-[100%] ${
                        (idx + 1) % 2 !== 0
                          ? "bg-[#E6F3FD] lg:h-auto sm:h-[104px] xs:h-[89px]"
                          : "bg-[#F3F8E6] lg:h-auto sm:h-[104px] xs:h-[89px]"
                      }`}
                    >
                      <p
                        className={`${
                          (idx + 1) % 2 !== 0
                            ? "text-[14px] sm:whitespace-normal whitespace-nowrap leading-[19.8px] xl:text-[16px] xl:leading-[22px] font-[400] text-[#000000] mb-[16px]"
                            : "text-subtitle-4 mb-[16px] font-regular text-[#000000]"
                        }`}
                      >
                        {project.title}
                      </p>
                      <h5
                        className={`${
                          (idx + 1) % 2 !== 0
                            ? "text-heading-5 font-semibold text-primary-text"
                            : "text-heading-6 font-semibold text-primary-text"
                        }`}
                      >
                        {project.numbers}
                        {(idx + 1) % 2 === 0 && project.description && (
                          <span
                            className="text-[14px] leading-[19.8px] xl:text-[16px] xl:leading-[22px] font-[400] text-[#000000] sm:hidden inline-block"
                            dangerouslySetInnerHTML={{
                              __html: project.description,
                            }}
                          />
                        )}
                      </h5>
                      {(idx + 1) % 2 === 0 && project.description && (
                        <span
                          className="text-[14px] leading-[19.8px] xl:text-[16px] xl:leading-[22px] font-[400] text-[#000000] sm:block hidden"
                          dangerouslySetInnerHTML={{
                            __html: project.description,
                          }}
                        />
                      )}
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Image & Technologies */}
            <div className="mb-[24px] lg:mb-[32px] relative">
              {item?.attributes?.image?.data?.attributes?.url && (
                <Image
                  src={`${url}${item?.attributes?.image?.data?.attributes?.url}`}
                  alt="image"
                  width={1182}
                  height={360}
                  className="w-full h-[360px] object-cover"
                />
              )}

              {item?.attributes?.blocks?.[0]?.technologies?.map(
                (tech: any, idx: number) => (
                  <div
                    key={`tech-${idx}`}
                    className="absolute w-full left-0 bottom-0 px-[0px] pt-[20px] md:p-[20px_25px] bg-[linear-gradient(180deg,rgba(176,217,247,0.3)_0%,#0086E680_100%)] backdrop-blur-[10px] overflow-hidden"
                  >
                    <div className="flex justify-between items-center md:flex-row flex-col gap-[20px]">
                      <p className="text-white  font-[600] text-[16px] leading-[22.2px]">
                        {tech?.title ?? ""}
                      </p>

                      {/* Splide Slider for Technologies */}
                      <div className="mb-[10px] sm:mb-[0px] w-[100%] sm:w-[40%] xl:w-[30%] ml-auto">
                        {tech?.technologies?.length > 0 && (
                          <Splide
                            options={{
                              type: "loop",
                              drag: "free",
                              focus: "center",
                              arrows: false,
                              pagination: false,
                              autoWidth: true,
                              gap: "8px",
                              autoScroll: {
                                speed: 1, // try values between 0.5 - 2 for smoothness
                                pauseOnHover: true,
                                pauseOnFocus: false,
                              },
                            }}
                            extensions={{ AutoScroll }}
                            className="marquee-splide"
                          >
                            {tech?.technologies?.map(
                              (stack: any, idx: number) => (
                                <SplideSlide key={`stack-${idx}`}>
                                  <div className="bg-white flex justify-center items-center w-[42px] h-[42px]">
                                    {stack?.image?.data?.attributes?.url ? (
                                      <Image
                                        width={42}
                                        height={42}
                                        src={`${url}${stack?.image?.data?.attributes?.url}`}
                                        alt={
                                          stack?.image?.data?.attributes?.name
                                        }
                                        className="w-[32px] h-[32px] object-cover"
                                        quality={100}
                                      />
                                    ) : (
                                      <div className="w-[32px] h-[32px] bg-[#E6EAF0]" />
                                    )}
                                  </div>
                                </SplideSlide>
                              )
                            )}
                          </Splide>
                        )}
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Description */}
            <p className="text-[#040405] font-[400] text-[16px] leading-[24.4px] xl:text-[20px] xl:leading-[28px]">
              {item?.attributes?.blocks?.[0]?.description ?? ""}
            </p>
          </div>
        ))}
      </div>
      {viewCase && (
        <div
          className="mt-3 view-all"
          style={{ display: "flex", justifyContent: "center" }}
        >
          <Button
            variant="small"
            className="my-0"
            onClick={handleClick}
            label=" View All Case Studies"
          />
        </div>
      )}
    </section>
  );
};

export default RequestProposalSolution;
